import { formatDateDisplay } from '@/utils/date';

import { isRecord } from './mappers';
import { type AuditText, auditMessage } from './text';

/** What the write path's sanitizer stores in place of a secret. */
export const AUDIT_REDACTED = '[REDACTED]';
/** What it stores in place of a value nested deeper than it records. */
export const AUDIT_TRUNCATED = '[TRUNCATED]';

/** One JSON value from `changes` or `metadata`, classified for display. */
export type AuditValue =
    | { kind: 'empty' }
    | { kind: 'boolean'; value: boolean }
    | { kind: 'number'; value: number }
    | { kind: 'text'; text: string }
    /** An ISO 8601 timestamp or date, which the page shows as a local date. */
    | { kind: 'date'; text: string }
    | { kind: 'hidden' }
    /** Nested too deep to record; like a hidden value, there is nothing to filter by. */
    | { kind: 'truncated' }
    | { kind: 'items'; count: number }
    | { kind: 'object' };

const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

function isIsoDate(text: string): boolean {
    if (ISO_TIMESTAMP.test(text)) return !Number.isNaN(new Date(text).getTime());
    return ISO_DATE.test(text);
}

export function toAuditValue(value: unknown): AuditValue {
    if (value === null || value === undefined) return { kind: 'empty' };
    if (typeof value === 'boolean') return { kind: 'boolean', value };
    if (typeof value === 'number') return { kind: 'number', value };
    if (typeof value === 'string') {
        if (value === AUDIT_REDACTED) return { kind: 'hidden' };
        if (value === AUDIT_TRUNCATED) return { kind: 'truncated' };
        return isIsoDate(value) ? { kind: 'date', text: value } : { kind: 'text', text: value };
    }
    if (Array.isArray(value)) return { kind: 'items', count: value.length };
    return isRecord(value) ? { kind: 'object' } : { kind: 'text', text: String(value) };
}

/** A calendar date has no time zone to convert from, so its parts are only reordered. */
function localDate(text: string): string {
    const dateOnly = ISO_DATE.exec(text);
    return dateOnly ? `${dateOnly[3]}/${dateOnly[2]}/${dateOnly[1]}` : formatDateDisplay(text);
}

function cut(text: string, maxLength: number | undefined): string {
    return maxLength !== undefined && text.length > maxLength
        ? `${text.slice(0, maxLength)}…`
        : text;
}

/** Strings longer than `maxLength` are cut, for one-line summaries. */
export function auditValueText(value: AuditValue, maxLength?: number): AuditText {
    switch (value.kind) {
        case 'empty':
            return auditMessage('auditLog.summary.value.empty');
        case 'boolean':
            return auditMessage(
                value.value ? 'auditLog.summary.value.yes' : 'auditLog.summary.value.no'
            );
        case 'number':
            return String(value.value);
        case 'text':
            return cut(value.text, maxLength);
        case 'date':
            return localDate(value.text);
        case 'hidden':
            return auditMessage('auditLog.summary.value.hidden');
        case 'truncated':
            return auditMessage('auditLog.summary.value.truncated');
        case 'items':
            return auditMessage('auditLog.summary.value.items', { count: value.count });
        case 'object':
            return auditMessage('auditLog.summary.value.object');
    }
}

/**
 * The text a filter button sends for a value, or null for a value no equality filter can
 * match: the backend compares JSON scalars only, and a redacted or cut-off value was never
 * stored.
 */
export function auditValueFilterText(value: AuditValue): string | null {
    switch (value.kind) {
        case 'boolean':
        case 'number':
            return String(value.value);
        case 'text':
        case 'date':
            return value.text;
        default:
            return null;
    }
}
